import type { CustomFieldDTO } from '@tempo-io/item-store-dto';
import { customFieldSchema } from '@tempo-io/item-store-dto';
import { z } from 'zod';
import { type TimelineLayoutDTO, timelineLayoutDTOSchema } from './layout-dto';
import {
  type PublishedEstimation,
  type PublishedEstimationScheme,
  type PublishedItemLevel,
  type PublishedItemStatus,
  type PublishedItemType,
  type PublishedTeam,
  publishedEstimationSchema,
  publishedEstimationSchemeSchema,
  publishedItemLevelSchema,
  publishedItemStatusSchema,
  publishedItemTypeSchema,
  publishedTeamSchema,
} from './published-lookup-dto';
import {
  type CustomerLookupEntry,
  customerLookupEntrySchema,
  type HeaderDisplayValue,
  headerDisplayValueSchema,
  type IdentifiableTItem,
  type ItemDetails,
  identifiableTItemSchema,
  itemDetailsSchema,
  type LegendItem,
  legendItemSchema,
  type PublishedMetadata,
  publishedMetadataSchema,
  type ResolvedCustomer,
  type ResolvedItem,
  type ResolvedUser,
  resolvedCustomerSchema,
  resolvedItemSchema,
  resolvedUserSchema,
} from './published-swimlane-dto';

export type {
  CustomerLookupEntry,
  HeaderDisplayValue,
  IdentifiableTItem,
  ItemDetails,
  LegendItem,
  PublishedEstimation,
  PublishedEstimationScheme,
  PublishedItemLevel,
  PublishedItemStatus,
  PublishedItemType,
  PublishedMetadata,
  PublishedTeam,
};

export {
  publishedEstimationSchema,
  publishedEstimationSchemeSchema,
  publishedItemLevelSchema,
  publishedItemStatusSchema,
  publishedItemTypeSchema,
  publishedTeamSchema,
};

export interface HeaderConfig {
  orderedHeaderIds: string[];
  hiddenHeaderIds: string[];
}

export interface PublishedTimelineResponse {
  items: IdentifiableTItem[];
  itemTypes: PublishedItemType[];
  itemLevels: PublishedItemLevel[];
  itemStatuses: PublishedItemStatus[];
  teams: PublishedTeam[];
  estimations: PublishedEstimation[];
  estimationSchemes: PublishedEstimationScheme[];
  mainHeaderConfig: HeaderConfig;
  subHeaderConfigs: Record<string, HeaderConfig>;
  layout: TimelineLayoutDTO;
  metadata: PublishedMetadata;
  itemDetails: Record<string, ItemDetails>;
  customFieldDefinitions: CustomFieldDTO[];
  hiddenFieldIds?: string[];
  legendItems?: LegendItem[];
  /**
   * Per-header display values keyed by header id. Server-resolved so
   * published timelines can render MULTI_CUSTOMER header chips without
   * running FIM hydration hooks in the browser.
   */
  headerDisplayValuesByHeaderId?: Record<string, HeaderDisplayValue[]>;
  /**
   * Server-resolved customer entity reference lookup, used by the published
   * item panel to render MULTI_CUSTOMER custom-field values as names.
   */
  customerLookup?: Record<string, CustomerLookupEntry>;
  resolvedUsers?: Record<string, ResolvedUser>;
  resolvedItems?: Record<string, ResolvedItem>;
  resolvedCustomers?: Record<string, ResolvedCustomer>;
}

export const headerConfigSchema = z.object({
  orderedHeaderIds: z.array(z.string()),
  hiddenHeaderIds: z.array(z.string()),
});

export const publishedTimelineResponseSchema = z.object({
  items: z.array(identifiableTItemSchema),
  itemTypes: z.array(publishedItemTypeSchema),
  itemLevels: z.array(publishedItemLevelSchema),
  itemStatuses: z.array(publishedItemStatusSchema),
  teams: z.array(publishedTeamSchema),
  estimations: z.array(publishedEstimationSchema).default([]),
  estimationSchemes: z.array(publishedEstimationSchemeSchema).default([]),
  mainHeaderConfig: headerConfigSchema,
  subHeaderConfigs: z.record(z.string(), headerConfigSchema),
  layout: timelineLayoutDTOSchema,
  metadata: publishedMetadataSchema,
  itemDetails: z.record(z.string(), itemDetailsSchema),
  customFieldDefinitions: z.array(customFieldSchema).default([]),
  hiddenFieldIds: z.array(z.string()).optional(),
  legendItems: z.array(legendItemSchema).optional(),
  headerDisplayValuesByHeaderId: z.record(z.string(), z.array(headerDisplayValueSchema)).optional(),
  customerLookup: z.record(z.string(), customerLookupEntrySchema).optional(),
  resolvedUsers: z.record(z.string(), resolvedUserSchema).optional(),
  resolvedItems: z.record(z.string(), resolvedItemSchema).optional(),
  resolvedCustomers: z.record(z.string(), resolvedCustomerSchema).optional(),
});
